/* eslint-disable react-hooks/exhaustive-deps */
"use client";

import { useLayoutEffect, useRef } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import ArrowLeft01Icon from "@hugeicons/core-free-icons/ArrowLeft01Icon";
import ArrowRight02Icon from "@hugeicons/core-free-icons/ArrowRight02Icon";
import ChartIncreaseIcon from "@hugeicons/core-free-icons/ChartIncreaseIcon";
import FlashIcon from "@hugeicons/core-free-icons/FlashIcon";
import UserIcon from "@hugeicons/core-free-icons/UserIcon";
import ShoppingCart01Icon from "@hugeicons/core-free-icons/ShoppingCart01Icon";
import {
  HeartFilledIcon,
  SentFilledIcon,
  CommentFilledIcon,
  ChartBarFilledIcon,
  ShieldFilledIcon,
  TimeFilledIcon,
  LockFilledIcon,
} from "@/lib/custom-icons";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  getHomepageMotionEnvironment,
  shouldUseHeavyHomepageMotion,
} from "@/components/LandingPage/mobile-motion-policy";

const SNEHA_AVATAR = "/sneha_28_72w.webp";

const FEATURE_TAGS = [
  {
    title: "Quick setup",
    description: "Go live without a long build.",
    icon: <TimeFilledIcon size={18} color="#ec4899" />,
    iconWrapClass:
      "border-pink-200 dark:border-pink-800/50 bg-pink-50 dark:bg-pink-950/30",
  },
  {
    title: "Instagram-safe flows",
    description: "Replies follow your rules.",
    icon: <ShieldFilledIcon size={18} color="#7c3aed" />,
    iconWrapClass:
      "border-violet-200 dark:border-violet-800/50 bg-violet-50 dark:bg-violet-950/30",
  },
  {
    title: "Built for creators",
    description: "Turn comments into follow-up.",
    icon: <LockFilledIcon size={18} color="#10b981" />,
    iconWrapClass:
      "border-emerald-200 dark:border-emerald-800/50 bg-emerald-50 dark:bg-emerald-950/30",
  },
] as const;

export default function HowItWorks() {
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const flowWrapRef = useRef<HTMLDivElement>(null);
  const stepsRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const connectorsRef = useRef<HTMLDivElement[]>([]);
  const connectorCurrentRefs = useRef<HTMLDivElement[]>([]);
  const card1CommentRef = useRef<HTMLDivElement>(null);
  const card1SkeletonRef = useRef<HTMLDivElement>(null);
  const card1SignalRef = useRef<HTMLImageElement>(null);
  const card2QuestionRef = useRef<HTMLDivElement>(null);
  const card2ReplyRef = useRef<HTMLDivElement>(null);
  const card2ActionsRef = useRef<HTMLDivElement>(null);
  const card3PopRef = useRef<HTMLDivElement>(null);
  const featureTagsRef = useRef<HTMLDivElement>(null);
  const statsCountRefs = useRef<(HTMLParagraphElement | null)[]>([]);
  const graphLineRef = useRef<SVGPathElement>(null);
  const graphFillRef = useRef<SVGPathElement>(null);

  useLayoutEffect(() => {
    if (!shouldUseHeavyHomepageMotion(getHomepageMotionEnvironment())) return;

    const section = sectionRef.current;
    const header = headerRef.current;
    const flowWrap = flowWrapRef.current;
    const steps = stepsRef.current;
    const cardsWrap = cardsRef.current;
    const card1Comment = card1CommentRef.current;
    const card1Skeleton = card1SkeletonRef.current;
    const card1Signal = card1SignalRef.current;
    const card2Question = card2QuestionRef.current;
    const card2Reply = card2ReplyRef.current;
    const card2Actions = card2ActionsRef.current;
    const card3Pop = card3PopRef.current;
    const featureTags = featureTagsRef.current;
    const graphLine = graphLineRef.current;
    const graphFill = graphFillRef.current;
    if (
      !section ||
      !header ||
      !flowWrap ||
      !steps ||
      !cardsWrap ||
      !card1Comment ||
      !card1Skeleton ||
      !card1Signal ||
      !card2Question ||
      !card2Reply ||
      !card2Actions ||
      !card3Pop ||
      !featureTags ||
      !graphLine ||
      !graphFill
    ) {
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    const cards = cardsWrap.querySelectorAll<HTMLElement>("[data-anim-card]");
    const connectors = connectorsRef.current.filter(Boolean);
    const connectorCurrents = connectorCurrentRefs.current.filter(Boolean);
    const connector1 = connectors[0];
    const connector2 = connectors[1];
    const card3Pops = card3Pop.querySelectorAll<HTMLElement>("[data-card3-pop]");
    const tags = featureTags.querySelectorAll<HTMLElement>("[data-feature-tag]");

    const graphLen = graphLine.getTotalLength();
    graphLine.style.strokeDasharray = `${graphLen}`;
    graphLine.style.strokeDashoffset = `${graphLen}`;

    const animateCounters = () => {
      const targets = [120, 86, 32];
      targets.forEach((target, i) => {
        const el = statsCountRefs.current[i];
        if (!el) return;
        const obj = { v: 0 };
        gsap.to(obj, {
          v: target,
          duration: 1.2,
          delay: i * 0.08,
          ease: "power2.out",
          onUpdate: () => {
            el.textContent = `${Math.round(obj.v)}`;
          },
        });
      });
    };

    gsap.set(header, { opacity: 0, y: 28 });
    gsap.set(steps, { opacity: 0, y: 20 });
    gsap.set(flowWrap, { maxWidth: "64rem" });
    gsap.set(cards, { opacity: 0, y: 26, scale: 0.97 });
    gsap.set(connectors, { opacity: 0, scale: 0.9 });
    gsap.set(connectorCurrents, { xPercent: -180, opacity: 0 });
    gsap.set(card1Comment, { opacity: 0, y: 18 });
    gsap.set(card1Signal, { opacity: 0, scale: 0.86, y: 4 });
    gsap.set(card2Question, { opacity: 0, y: 14, scale: 0.97 });
    gsap.set(card2Reply, { opacity: 0, y: 16, scale: 0.98 });
    gsap.set(card2Actions, { opacity: 0, y: 16 });
    gsap.set(card3Pops, { opacity: 0, y: 14, scale: 0.98 });
    gsap.set(tags, { opacity: 0, y: 18 });
    gsap.set(graphFill, { opacity: 0 });
    statsCountRefs.current.forEach((el) => {
      if (el) el.textContent = "0";
    });
    const tl = gsap.timeline({ paused: true });
    tl.timeScale(1.2);
    tl.to(header, { opacity: 1, y: 0, duration: 0.58, ease: "power3.out" })
      .to(steps, { opacity: 1, y: 0, duration: 0.54, ease: "power2.out" }, "-=0.44")
      .to(flowWrap, { maxWidth: "80rem", duration: 0.72, ease: "power2.out" }, "-=0.38")
      .to(
        cards,
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.5,
          stagger: 0.11,
          ease: "back.out(1.08)",
        },
        "-=0.28",
      )
      .to(card1Comment, { opacity: 1, y: 0, duration: 0.38, ease: "power2.out" }, "+=0.03")
      .to(
        connector1,
        {
          opacity: 1,
          scale: 1,
          duration: 0.4,
          ease: "power2.out",
        },
        "-=0.08",
      )
      .add(() => {
        gsap.to(card1Skeleton, {
          opacity: 0.6,
          y: -2,
          duration: 1.05,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
        });
        gsap.to(card1Signal, {
          opacity: 1,
          scale: 1.05,
          y: -2,
          duration: 0.44,
          ease: "power2.out",
          repeat: -1,
          yoyo: true,
          repeatDelay: 2.05,
        });
        const current1 = connectorCurrentRefs.current[0];
        if (current1) {
          gsap.to(current1, {
            xPercent: 220,
            opacity: 1,
            duration: 0.96,
            ease: "sine.inOut",
            repeat: -1,
            repeatDelay: 0.28,
          });
        }
      })
      .to(card2Question, { opacity: 1, y: 0, scale: 1, duration: 0.34, ease: "power2.out" }, "+=0.02")
      .to(card2Reply, { opacity: 1, y: 0, scale: 1, duration: 0.38, ease: "power2.out" }, "+=0.22")
      .to(card2Actions, { opacity: 1, y: 0, duration: 0.34, ease: "power2.out" }, "-=0.22")
      .to(
        connector2,
        {
          opacity: 1,
          scale: 1,
          duration: 0.4,
          ease: "power2.out",
        },
        "-=0.06",
      )
      .add(() => {
        const current2 = connectorCurrentRefs.current[1];
        if (current2) {
          gsap.to(current2, {
            xPercent: 220,
            opacity: 1,
            duration: 0.96,
            ease: "sine.inOut",
            repeat: -1,
            repeatDelay: 0.28,
          });
        }
      }, "<")
      .to(card3Pops, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.32,
        stagger: 0.07,
        ease: "power2.out",
      })
      .to(
        graphLine,
        {
          strokeDashoffset: 0,
          duration: 0.92,
          ease: "power2.out",
        },
        "-=0.12",
      )
      .to(graphFill, { opacity: 1, duration: 0.48, ease: "power2.out" }, "-=0.4")
      .add(animateCounters, "-=0.86")
      .to(
        tags,
        {
          opacity: 1,
          y: 0,
          duration: 0.38,
          stagger: 0.085,
          ease: "power2.out",
        },
        "-=0.18",
      );

    const trigger = ScrollTrigger.create({
      trigger: section,
      start: "top 82%",
      once: true,
      onEnter: () => tl.play(),
    });

    return () => {
      gsap.killTweensOf(card1Skeleton);
      gsap.killTweensOf(card1Signal);
      connectorCurrentRefs.current.forEach((el) => {
        if (el) gsap.killTweensOf(el);
      });
      trigger.kill();
      tl.kill();
      ScrollTrigger.getAll().forEach((st) => {
        if (st.trigger === section) st.kill();
      });
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="w-full bg-[#FBFBFB] dark:bg-[#101113] border-t border-border/40"
      id="how-it-works"
    >
      <div className="container mx-auto container-padding section-y">
      {/* ── Header ─────────────────────────────────────────── */}
      <div ref={headerRef} className="mx-auto max-w-3xl text-center">
        <h2 className="text-[clamp(2rem,4.6vw,3.25rem)] font-extrabold leading-[1.15] tracking-tight text-foreground">
          Launch your first automation in{" "}
          <span className="bg-primary-gradient bg-clip-text text-transparent">3 simple steps</span>
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-muted-foreground">
          Pick what triggers it, write the message, and let Zapify reply while the conversation is fresh.
        </p>
      </div>

      {/* ── Full 7xl wrapper ─────────────────────────────────── */}
      <div ref={flowWrapRef} className="mx-auto mt-12 w-full max-w-7xl">

        {/* ── Step Labels Row ── */}
        <div ref={stepsRef} className="mb-10 hidden lg:grid lg:grid-cols-[1fr_148px_1fr_148px_1fr]">
          {/* Step 1 */}
          <div>
            <div className="flex items-center gap-2.5">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-full border-2 border-pink-400 bg-transparent text-sm font-bold text-pink-500">1</span>
              <div>
                <span className="font-semibold text-foreground">Choose your trigger</span>
                <div className="mt-0.5 h-[4px] w-8 rounded-full bg-pink-400" />
              </div>
            </div>
            <p className="mt-1.5 pl-[46px] text-sm leading-relaxed text-muted-foreground max-w-[220px]">
              Start a DM when someone comments a keyword, drops an emoji, or interacts with a post or reel.
            </p>
          </div>
          {/* Arrow 1 */}
          <div className="flex items-center pt-3.5 -ml-6">
            <div className="flex-1 border-t-2 border-dashed border-pink-300/70 dark:border-pink-700/50" />
            <HugeiconsIcon icon={ArrowRight02Icon} size={14} color="currentColor" className="text-pink-300 dark:text-pink-700" />
          </div>
          {/* Step 2 */}
          <div>
            <div className="flex items-center gap-2.5">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-full border-2 border-violet-400 bg-transparent text-sm font-bold text-violet-500">2</span>
              <div>
                <span className="font-semibold text-foreground">Reply in your voice</span>
                <div className="mt-0.5 h-[4px] w-8 rounded-full bg-violet-400" />
              </div>
            </div>
            <p className="mt-1.5 pl-[46px] text-sm leading-relaxed text-muted-foreground max-w-[220px]">
              Send a personalized DM, share your link, or ask them to follow before you deliver the content.
            </p>
          </div>
          {/* Arrow 2 */}
          <div className="flex items-center pt-3.5 -ml-6">
            <div className="flex-1 border-t-2 border-dashed border-emerald-300/70 dark:border-emerald-700/50" />
            <HugeiconsIcon icon={ArrowRight02Icon} size={14} color="currentColor" className="text-emerald-300 dark:text-emerald-700" />
          </div>
          {/* Step 3 */}
          <div>
            <div className="flex items-center gap-2.5">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-full border-2 border-emerald-400 bg-transparent text-sm font-bold text-emerald-500">3</span>
              <div>
                <span className="font-semibold text-foreground">Capture and track</span>
                <div className="mt-0.5 h-[4px] w-8 rounded-full bg-emerald-400" />
              </div>
            </div>
            <p className="mt-1.5 pl-[46px] text-sm leading-relaxed text-muted-foreground max-w-[220px]">
              Collect leads, send people to the right offer, and see which posts drive results.
            </p>
          </div>
        </div>

        {/* ── Cards Row ─────────────────────────────────────── */}
        <div ref={cardsRef} className="grid gap-4 lg:gap-0 lg:grid-cols-[1fr_148px_1fr_148px_1fr]">

          {/* ════ Step 1 Header (Mobile) ════ */}
          <div className="lg:hidden -mb-2">
            <div className="flex items-center gap-2.5">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-pink-400 bg-transparent text-sm font-bold text-pink-500">1</span>
              <div>
                <span className="font-semibold text-foreground text-sm">Choose your trigger</span>
                <div className="mt-0.5 h-[3px] w-6 rounded-full bg-pink-400" />
              </div>
            </div>
            <p className="mt-2 pl-[42px] text-xs leading-relaxed text-muted-foreground">
              Start a DM when someone comments a keyword, drops an emoji, or interacts with a post or reel.
            </p>
          </div>

          {/* ════ Card 1: Instagram Post ════ */}
          <article data-anim-card className="rounded-[1.25rem] border border-border/50 bg-white dark:bg-card">
            {/* Instagram header */}
            <div className="flex items-center justify-between px-4 pt-4 pb-2.5">
              <div className="flex items-center gap-2">
                <img
                  src="/Instagram_icon_how_it_works.webp"
                  alt="Instagram"
                  className="size-[26px] rounded-[6px] object-cover"
                />
                <div className="flex items-start gap-0.5 flex-col -line-clamp-1 leading-none">
                  <span className="text-[13px] font-semibold text-foreground">your.brand</span>
                  <span className="text-[11px] font-semibold text-muted-foreground/90">now</span>
                </div>
              </div>
              <span className="text-[18px] font-bold leading-none tracking-[0.14em] text-muted-foreground/50">···</span>
            </div>

            {/* Post image */}
            <div className="mx-3.5 overflow-hidden rounded-2xl">
              <img
                src="/product-mockup.jpg"
                alt="Product"
                className="h-52 w-full object-cover object-top"
                loading="lazy"
                decoding="async"
              />
            </div>

            {/* Pop-out comment card */}
            <div
              ref={card1CommentRef}
              className="relative z-10 -ml-6 mr-10 -mt-[3.5rem] rounded-2xl border border-border/50 bg-white dark:bg-zinc-900 px-3.5 py-3 shadow-[0_8px_28px_-6px_rgba(0,0,0,0.20)] dark:shadow-[0_8px_28px_-6px_rgba(0,0,0,0.55)]"
            >
              {/* Signal icon — top-left corner */}
              <img
                ref={card1SignalRef}
                src="/3barCornerForTopLeft.png"
                alt=""
                className="absolute -left-7 -top-6 w-8 select-none pointer-events-none dark:opacity-60"
              />
              <div className="flex items-start gap-2.5">
                <img
                  src={SNEHA_AVATAR}
                  alt="sneha_28"
                  className="size-9 shrink-0 rounded-full object-cover"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[13px] font-semibold text-foreground">sneha_28</p>
                    <p className="shrink-0 text-[12px] text-muted-foreground/90 font-semibold">now</p>
                  </div>
                  <p className="mt-0.5 text-[13px] text-foreground">What&apos;s the price?</p>
                </div>
              </div>
            </div>

            {/* Social icons */}
            <div className="flex items-center gap-4 px-5 py-3">
              <HeartFilledIcon size={18} color="#ef4444" />
              <CommentFilledIcon size={18} color="currentColor" className="text-muted-foreground/60" />
              <SentFilledIcon size={18} color="currentColor" className="text-muted-foreground/60" />
            </div>

            {/* Skeleton row */}
            <div ref={card1SkeletonRef} className="mx-3.5 mb-4 flex items-center gap-2.5">
              <div className="size-7 shrink-0 rounded-full bg-muted/80 dark:bg-muted" />
              <div className="flex flex-1 flex-col gap-1.5">
                <div className="h-2 w-[68%] rounded-full bg-muted/80 dark:bg-muted" />
                <div className="h-2 w-[45%] rounded-full bg-muted/80 dark:bg-muted" />
              </div>
            </div>
          </article>

          {/* ════ Connector 1: Zap ════ */}
          <div
            ref={(el) => {
              if (el) connectorsRef.current[0] = el;
            }}
            className="relative hidden h-full items-center justify-center lg:flex"
          >
            <div className="absolute inset-x-0 top-1/2 -translate-y-px border-t-2 border-dashed border-stone-300/65 dark:border-zinc-600/60" />
            <div
              ref={(el) => {
                if (el) connectorCurrentRefs.current[0] = el;
              }}
              className="pointer-events-none absolute left-0 top-1/2 h-[2px] w-16 -translate-y-1/2 rounded-full bg-gradient-to-r from-transparent via-pink-400/90 to-transparent opacity-0"
            />
            <div className="relative z-10 flex items-center gap-0.5">
              <div className="flex size-12 items-center justify-center rounded-full border border-pink-200 dark:border-pink-800/50 bg-white dark:bg-zinc-900 shadow-[0_2px_14px_-4px_rgba(0,0,0,0.16)] dark:shadow-[0_2px_14px_-4px_rgba(0,0,0,0.45)]">
                <HugeiconsIcon icon={FlashIcon} size={22} color={"currentColor"} className="fill-pink-500 text-pink-500" />
              </div>
              <HugeiconsIcon icon={ArrowRight02Icon} size={18} color={"currentColor"} className="size-4 shrink-0 text-stone-400 dark:text-zinc-500" />
            </div>
          </div>

          {/* ════ Step 2 Header (Mobile) ════ */}
          <div className="lg:hidden -mb-2">
            <div className="flex items-center gap-2.5">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-violet-400 bg-transparent text-sm font-bold text-violet-500">2</span>
              <div>
                <span className="font-semibold text-foreground text-sm">Reply in your voice</span>
                <div className="mt-0.5 h-[3px] w-6 rounded-full bg-violet-400" />
              </div>
            </div>
            <p className="mt-2 pl-[42px] text-xs leading-relaxed text-muted-foreground">
              Send a personalized DM, share your link, or ask them to follow before you deliver the content.
            </p>
          </div>

          {/* ════ Card 2: DM Chat ════ */}
          <article data-anim-card className="rounded-[1.25rem] border border-border/50 bg-white dark:bg-card">
            {/* Chat header */}
            <div className="flex items-center gap-2.5 border-b border-border/40 px-4 py-3">
              <HugeiconsIcon icon={ArrowLeft01Icon} size={16} color={"currentColor"} className="size-4 shrink-0 text-muted-foreground/60" />
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-white dark:bg-zinc-800 border border-border/40 overflow-hidden shadow-sm">
                <img src="/zapify-favicon-20w.webp" alt="Zapify" width={20} height={20} className="size-5 object-contain" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold leading-none text-foreground">Zapify Automation</p>
                <p className="mt-0.5 text-[11px] font-medium text-emerald-500 dark:text-emerald-400">Active now</p>
              </div>
              <span className="text-[18px] font-bold leading-none tracking-[0.14em] text-muted-foreground/50">···</span>
            </div>

            <div className="space-y-3 p-4">
              {/* User message — RIGHT */}
              <div ref={card2QuestionRef} className="flex items-end justify-end gap-2">
                <div className="max-w-[72%] rounded-2xl rounded-br-sm bg-muted/50 border border-border/40 px-3.5 py-2.5">
                  <p className="text-[12.5px] text-foreground">
                    What&apos;s the price?
                  </p>
                </div>
                <img
                  src={SNEHA_AVATAR}
                  alt="sneha_28"
                  className="size-7 shrink-0 rounded-full object-cover"
                />
              </div>

              {/* Automation reply — LEFT */}
              <div ref={card2ReplyRef} className="flex items-start gap-2">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-white dark:bg-zinc-800 border border-border/40 overflow-hidden shadow-sm mt-0.5">
                  <img src="/zapify-favicon-20w.webp" alt="Zapify" width={16} height={16} className="size-4 object-contain" />
                </span>
                <div className="max-w-[82%] rounded-2xl rounded-tl-sm bg-violet-100 dark:bg-violet-950/50 px-3.5 py-2.5">
                  <p className="text-[12.5px] leading-relaxed text-violet-900 dark:text-violet-200">
                    Hi Sneha! 👋<br />
                    The price of this product is <strong>₹1,499</strong>.<br />
                    Want me to send you the link?
                  </p>
                </div>
              </div>

              {/* Action buttons */}
              <div ref={card2ActionsRef} className="flex gap-2 pl-9">
                <span className="cursor-default rounded-full border border-pink-300 dark:border-pink-700/60 bg-white dark:bg-zinc-800/80 px-3.5 py-1.5 text-[11.5px] font-semibold text-pink-600 dark:text-pink-400">
                  Yes, share
                </span>
                <span className="cursor-default rounded-full border border-border/60 bg-white dark:bg-zinc-800/80 px-3.5 py-1.5 text-[11.5px] font-semibold text-muted-foreground">
                  Not now
                </span>
              </div>
            </div>

            {/* Message input */}
            <div className="mx-4 mb-4 flex items-center justify-between rounded-full border border-border/60 bg-muted/30 dark:bg-muted/20 px-4 py-2.5">
              <span className="text-[13px] text-muted-foreground">Message…</span>
              <div className="flex items-center gap-2">
                <span className="text-[18px] leading-none text-muted-foreground/60">☺</span>
                <span className="flex size-6 items-center justify-center rounded-full bg-pink-500">
                  <SentFilledIcon size={14} color="#ffffff" />
                </span>
              </div>
            </div>
          </article>

          {/* ════ Connector 2: BarChart ════ */}
          <div
            ref={(el) => {
              if (el) connectorsRef.current[1] = el;
            }}
            className="relative hidden h-full items-center justify-center lg:flex"
          >
            <div className="absolute inset-x-0 top-1/2 -translate-y-px border-t-2 border-dashed border-stone-300/65 dark:border-zinc-600/60" />
            <div
              ref={(el) => {
                if (el) connectorCurrentRefs.current[1] = el;
              }}
              className="pointer-events-none absolute left-0 top-1/2 h-[2px] w-16 -translate-y-1/2 rounded-full bg-gradient-to-r from-transparent via-emerald-400/90 to-transparent opacity-0"
            />
            <div className="relative z-10 flex items-center gap-0.5">
              <div className="flex size-12 items-center justify-center rounded-full border border-emerald-200 dark:border-emerald-800/50 bg-white dark:bg-zinc-900 shadow-[0_2px_14px_-4px_rgba(0,0,0,0.16)] dark:shadow-[0_2px_14px_-4px_rgba(0,0,0,0.45)]">
                <ChartBarFilledIcon size={22} color="#10b981" />
              </div>
              <HugeiconsIcon icon={ArrowRight02Icon} size={18} color={"currentColor"} className="size-4 shrink-0 text-stone-400 dark:text-zinc-500" />
            </div>
          </div>

          {/* ════ Step 3 Header (Mobile) ════ */}
          <div className="lg:hidden -mb-2">
            <div className="flex items-center gap-2.5">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-emerald-400 bg-transparent text-sm font-bold text-emerald-500">3</span>
              <div>
                <span className="font-semibold text-foreground text-sm">Capture and track</span>
                <div className="mt-0.5 h-[3px] w-6 rounded-full bg-emerald-400" />
              </div>
            </div>
            <p className="mt-2 pl-[42px] text-xs leading-relaxed text-muted-foreground">
              Collect leads, send people to the right offer, and see which posts drive results.
            </p>
          </div>

          {/* ════ Card 3: CRM / Analytics ════ */}
          <article data-anim-card className="rounded-[1.25rem] border border-border/50 bg-white dark:bg-card p-4">
            {/* Header */}
            <div ref={card3PopRef} className="flex flex-col gap-0">
            <div data-card3-pop className="flex items-center justify-between">
              <span className="text-[14px] font-semibold text-foreground">Lead captured 🎉</span>
              <span className="rounded-full bg-emerald-100 dark:bg-emerald-900/30 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">
                New lead
              </span>
            </div>

            {/* Lead name + platform icons */}
            <div data-card3-pop className="mt-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img
                  src={SNEHA_AVATAR}
                  alt="sneha_28"
                  className="size-8 shrink-0 rounded-full object-cover"
                />
                <p className="text-[13px] font-semibold text-foreground">sneha_28</p>
              </div>
              <div className="flex items-center gap-1.5">
                <img
                  src="/Instagram_icon_how_it_works.webp"
                  alt="Instagram"
                  className="size-[20px] rounded-[4px] object-cover"
                />
                <CommentFilledIcon size={20} color="#f472b6" />
              </div>
            </div>

            {/* Source / Trigger / Time */}
            <div data-card3-pop className="mt-3 grid w-full grid-cols-3">
              <div className="flex flex-col gap-1">
                <p className="text-[9px] uppercase tracking-wide text-muted-foreground/70">Source</p>
                <p className="text-[11px] font-semibold text-foreground">Instagram</p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-[9px] uppercase tracking-wide text-muted-foreground/70">Trigger</p>
                <p className="text-[11px] font-semibold text-foreground">Comment: Price?</p>
              </div>
              <div className="flex flex-col gap-1 pl-4">
                <p className="text-[9px] uppercase tracking-wide text-muted-foreground/70">Time</p>
                <p className="text-[11px] font-semibold text-foreground">Just now</p>
              </div>
            </div>

            {/* Revenue impact */}
            <div data-card3-pop className="mt-3 flex items-center justify-between pt-3.5">
              <p className="text-[12px] font-medium text-muted-foreground">Revenue impact</p>
              <span className="inline-flex items-center gap-1 rounded-sm border border-border/50 bg-muted/30 dark:bg-muted/20 px-2 py-0.5 text-[10.5px] text-muted-foreground">
                This month
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </span>
            </div>
            <div data-card3-pop className="mt-0.5 flex items-baseline gap-2">
              <p className="text-[1.5rem] font-extrabold tracking-tight text-foreground">
                ₹ 2,45,000
              </p>
              <span className="flex items-center gap-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                <HugeiconsIcon icon={ChartIncreaseIcon} size={16} color={"currentColor"} className="size-2.5" />
                +28.6%
              </span>
            </div>

            {/* SVG line chart */}
            <div data-card3-pop className="mt-4">
              <svg viewBox="0 0 200 54" className="h-12 w-full" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="hiw-chart" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity="0.22" />
                    <stop offset="100%" stopColor="#10b981" stopOpacity="0.01" />
                  </linearGradient>
                </defs>
                <path
                  ref={graphLineRef}
                  d="M0 48 L12 40 L22 45 L34 34 L44 38 L56 26 L66 31 L78 20 L90 26 L102 14 L114 20 L126 10 L138 16 L150 6 L162 12 L174 4 L186 9 L200 2"
                  stroke="#10b981"
                  strokeWidth="1.8"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  ref={graphFillRef}
                  d="M0 48 L12 40 L22 45 L34 34 L44 38 L56 26 L66 31 L78 20 L90 26 L102 14 L114 20 L126 10 L138 16 L150 6 L162 12 L174 4 L186 9 L200 2 L200 54 L0 54Z"
                  fill="url(#hiw-chart)"
                />
              </svg>
            </div>

            {/* Stats row */}
            <div data-card3-pop className="mt-2 grid grid-cols-3 gap-2">
              {/* Leads */}
              <div className="flex flex-col gap-2 rounded-lg bg-blue-50 dark:bg-blue-950/30 px-2.5 py-2.5">
                <p className="text-[9.5px] font-medium text-blue-400 dark:text-blue-300">Leads</p>
                <div className="flex items-center justify-between">
                  <p
                    ref={(el) => {
                      statsCountRefs.current[0] = el;
                    }}
                    className="text-[15px] font-bold leading-none text-foreground"
                  >
                    120
                  </p>
                  <HugeiconsIcon icon={UserIcon} size={18} color="#3b82f6" />
                </div>
              </div>
              {/* Convos */}
              <div className="flex flex-col gap-2 rounded-lg bg-orange-50 dark:bg-orange-950/30 px-2.5 py-2.5">
                <p className="text-[9.5px] font-medium text-orange-400 dark:text-orange-300">Convos</p>
                <div className="flex items-center justify-between">
                  <p
                    ref={(el) => {
                      statsCountRefs.current[1] = el;
                    }}
                    className="text-[15px] font-bold leading-none text-foreground"
                  >
                    86
                  </p>
                  <CommentFilledIcon size={18} color="#f97316" />
                </div>
              </div>
              {/* Sales */}
              <div className="flex flex-col gap-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 px-2.5 py-2.5">
                <p className="text-[9.5px] font-medium text-emerald-500 dark:text-emerald-400">Sales</p>
                <div className="flex items-center justify-between">
                  <p
                    ref={(el) => {
                      statsCountRefs.current[2] = el;
                    }}
                    className="text-[15px] font-bold leading-none text-foreground"
                  >
                    32
                  </p>
                  <HugeiconsIcon icon={ShoppingCart01Icon} size={18} color="#10b981" />
                </div>
              </div>
            </div>
            </div>
          </article>
        </div>

        {/* ── Bottom feature tags ─────────────────────────── */}
        <div ref={featureTagsRef} className="mt-10 pt-6 sm:mt-12 sm:pt-10">
          <div className="hidden flex-wrap items-center justify-center gap-x-12 gap-y-5 sm:flex">
            {FEATURE_TAGS.map((tag) => (
              <div key={tag.title} data-feature-tag className="flex items-center gap-3">
                <span
                  className={`flex size-9 items-center justify-center rounded-full border ${tag.iconWrapClass}`}
                >
                  {tag.icon}
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground">{tag.title}</p>
                  <p className="text-xs text-muted-foreground">{tag.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="overflow-hidden sm:hidden">
            <div className="flex w-max min-w-full animate-testimonial-marquee items-center gap-3 pr-3">
              {[...FEATURE_TAGS, ...FEATURE_TAGS].map((tag, i) => (
                <div
                  key={`${tag.title}-${i}`}
                  data-feature-tag
                  className="flex items-center gap-2.5 rounded-full border border-border/60 bg-white/92 px-3 py-2 dark:bg-zinc-900/92"
                >
                  <span
                    className={`flex size-8 items-center justify-center rounded-full border ${tag.iconWrapClass}`}
                  >
                    {tag.icon}
                  </span>
                  <div className="whitespace-nowrap">
                    <p className="text-[13px] font-semibold leading-tight text-foreground">{tag.title}</p>
                    <p className="text-[11px] leading-tight text-muted-foreground">{tag.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      </div>
    </section>
  );
}
